import { Component, Input } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  Observable,
  take,
} from 'rxjs';
import { GenericApiCall } from 'src/app/common/store/types';
import { formatDate } from 'src/app/shared/helper/datehelper';
import {
  removeValidators,
  setValidators,
} from 'src/app/shared/helper/formhelper';
import { debugLog, filterTrue } from 'src/app/shared/pipe/rxjs/operators';
import { ModalService } from 'src/app/shared/service/modal.service';
import { AccountingService } from 'src/app/shared/store/accounting/accounting.service';
import {
  AccountModel,
  AccountMoveInstanceParams,
  AccountMoveLineModel,
  AccountMoveModel,
  AccountMovePeriodModel,
  APPLICATION_FEES_ID,
  INSURANCE_CLAIM_FEES_ID,
  RECURRING_FEES_ID,
} from 'src/app/shared/store/accounting/types';
import { FormService } from 'src/app/shared/store/form/form.service';
import { SubHeaderService } from 'src/app/shared/store/subheader/subheader.service';
import {
  DataType,
  DropdownStyle,
  SourceEnum,
} from 'src/app/shared/store/typeahead/types';
import { Entity, View } from 'src/app/shared/store/view/types';
import { ViewService } from 'src/app/shared/store/view/views.service';
import { ApplicationFeeRuleModel } from '../../../store/application-fee-rule/types';
import { ApplicationFeeRuleService } from '../../../store/application-fee-rule/application-fee-rule.service';
import { AmountType } from '../../../store/commissions/commission-plan-rule/types';
import { ShowSignValueTypePipe } from '../../../pipe/show-sign-value-type.pipe';
import { getAmountSign } from 'src/app/shared/helper/amounthelper';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { deepCopy } from '../../../helper/objecthelper';
import { MAX_DATE_MASK_SAFE_GUARD } from '../../../constants/date.constants';
import {
  ModalComponent,
  ModalComponentKey,
} from 'src/app/shared/service/modal-component';

@UntilDestroy()
@ModalComponent(ModalComponentKey.AccountMoveAdd)
@Component({
  selector: 'app-account-move-add-content',
  templateUrl: './account-move-add-content.component.html',
  styleUrls: ['./account-move-add-content.component.scss'],
  standalone: false,
})
export class AccountMoveAddContentComponent {
  @Input() modalRandomIdentifier: number;
  @Input() contractId: number;
  /** Renseigné quand la modale est ouverte depuis l'onglet quittances d'un sinistre. */
  @Input() insuranceClaimId?: number;
  @Input() isClient: boolean;
  /** Absent quand la ligne de produit du contrat ne porte pas de compagnie d'assurance. */
  @Input() contractInsuranceCompanyContactId?: number;
  @Input() accountMoveId: number;
  @Input() source: SourceEnum = SourceEnum.Component;

  view$: Observable<View>;
  paymentForm: FormGroup;
  accountMoveInstance$: Observable<AccountMoveModel>;
  accountMovePeriodsModels$: Observable<AccountMovePeriodModel[]>;
  account$: Observable<AccountModel>;
  accountId: number = 0;
  applicationFeeRuleModel$: Observable<ApplicationFeeRuleModel>;

  accountMoveModel$: Observable<AccountMoveModel>;
  isEdit: boolean = false;
  accountMoveModel: AccountMoveModel;

  apiCall: GenericApiCall<any, any>;
  isRecurringFees: boolean;
  accountToDomain: string = '';

  defaultAmountSign: AmountType = 'Monetary';
  unitPricePlaceholder: string;
  unitPriceSuffix: string;
  saveValue: { minValue: ValidatorFn; maxValue: ValidatorFn } | undefined;

  protected readonly DataType = DataType;
  protected readonly DropdownStyle = DropdownStyle;
  protected readonly Entity = Entity;
  maxDateMaskSafeGuard: string = MAX_DATE_MASK_SAFE_GUARD;

  constructor(
    private readonly subHeaderService: SubHeaderService,
    private readonly viewService: ViewService,
    private readonly formBuilder: FormBuilder,
    private readonly formService: FormService,
    private readonly accountingService: AccountingService,
    private readonly dynamicService: ModalService,
    private readonly modalService: NgbModal,
    private readonly applicationFeeRuleService: ApplicationFeeRuleService,
    private readonly showSignValueType: ShowSignValueTypePipe,
  ) {
    this.view$ = this.viewService.getViews.value$;
    this.accountMovePeriodsModels$ = this.accountingService.getPeriods.value$;
    this.accountMoveInstance$ = this.accountingService.createInstance.value$;
    this.account$ = this.accountingService.getAccountByContactId.value$;
    this.applicationFeeRuleModel$ =
      this.applicationFeeRuleService.getByContractId.value$;
    this.accountMoveModel$ = this.accountingService.getById.value$;
  }

  ngOnInit(): void {
    this.isEdit = !!this.accountMoveId;

    this.unitPricePlaceholder = this.unitPriceSuffix = getAmountSign(
      this.defaultAmountSign,
    );

    const insuranceCompanyContactId = this.contractInsuranceCompanyContactId;

    const brokerDomain: string = '(accountType=2,active=1)';
    //sans compagnie, on n'a pas de clause à ajouter : on retombe sur le seul domaine courtier.
    const insuranceCompanyDomain: string = insuranceCompanyContactId
      ? '|(contactId=' + insuranceCompanyContactId + ',active=1,accountType=1)'
      : '';
    this.accountToDomain = brokerDomain + insuranceCompanyDomain;
    this.isRecurringFees = false;
    if (insuranceCompanyContactId) {
      this.accountingService.getAccountByContactId.call(
        insuranceCompanyContactId.toString(),
      );
    }

    if (this.isEdit) {
      this.apiCall = this.accountingService.getById;
      this.accountingService.getById.call(this.accountMoveId.toString());
    } else {
      this.apiCall = this.accountingService.getPeriods;
      let accountMoveInstanceParams: AccountMoveInstanceParams = {
        contractId: this.contractId,
        insuranceClaimId: this.insuranceClaimId,
      };

      accountMoveInstanceParams.moveType = this.isClient
        ? 'CustomerInvoice'
        : 'VendorInvoice';

      this.accountingService.getPeriods.call(this.contractId);
      this.accountingService.createInstance.call(accountMoveInstanceParams);
    }

    this.paymentForm = this.formBuilder.group({
      accountMoveName: [undefined, [Validators.required]],
      accountMoveLineName: [],
      accountMoveTypeId: [],
      contactId: [undefined, [Validators.required]],
      unitPrice: [undefined, [Validators.required]],
      moveType: [undefined],
      salesPersonId: [],
      creditAccountId: [undefined, [Validators.required]],
      debitAccountId: [undefined, [Validators.required]],
      contractId: [],
      accountMovePaymentStatus: [],
      accountMoveStatus: [],
      autoPost: [],
      quantity: [0],
      debit: [0],
      credit: [0],
      currencyId: [],
      discount: [0],
      description: [],
      paymentMode: [],
      directionSign: [],
      paymentReference: [''],
      status: [],
      sequence: [],
      accountMoveLinePaymentStatus: [],
      accountMovePeriod: [],
      periodStartDate: [],
      periodEndDate: [],
    });

    this.accountMoveInstance$
      .pipe(
        filter((x) => !!x && !this.isEdit),
        untilDestroyed(this),
      )
      .subscribe((x) => {
        this.paymentForm.patchValue({
          accountMovePaymentStatus: x.paymentStatus,
          contractId: x.contractId,
          directionSign: x.directionSign,
          status: x.status,
          moveType: x.moveType,
          autoPost: x.autoPost,
          dueDate: x.deliveryDate,
          salesPersonId: x.salesPersonId,
          accountMoveStatus: x.status,
          creationDate: new Date(),
          currencyId: x.accountMoveLines[0].currencyId,
          sequence: x.accountMoveLines[0].sequence,
          quantity: x.accountMoveLines[0].quantity,
          // unitPrice: x.accountMoveLines[0].unitPrice,
          discount: x.accountMoveLines[0].discount,
          debit: x.accountMoveLines[0].debit,
          credit: x.accountMoveLines[0].credit,
          accountMoveLinePaymentStatus: x.accountMoveLines[0].paymentStatus,
          contactId: x.contactId,
          debitAccountId: this.isClient ? x.debitAccountId : undefined,
          creditAccountId: this.isClient ? undefined : x.creditAccountId,
        });
        // if (!this.insuranceClaimId)
        //   this.paymentForm.patchValue({
        //     accountMoveName: x.name,
        //   });

        //Sur un sinistre, l'instance porte déjà le montant par défaut du type de sinistre
        //(ClaimType.DefaultFeeAmount, posé par le backend dans CreateEntity) : le front n'a donc pas
        //à connaître la règle. On pousse une chaîne, comme la valeur que produit le masque, pour que
        //le backend ne reçoive pas un décimal à virgule qu'il ne saurait pas binder.
        const defaultAmount = x.accountMoveLines?.[0]?.unitPrice;
        if (
          this.insuranceClaimId &&
          defaultAmount != null &&
          defaultAmount != 0
        ) {
          this.paymentForm.get('unitPrice')?.setValue(defaultAmount.toString());
        }
      });

    this.accountMoveModel$
      .pipe(
        filter((x) => !!x && this.isEdit),
        untilDestroyed(this),
      )
      .subscribe((x) => {
        this.accountMoveModel = x;
        this.paymentForm.patchValue({
          accountMoveName: x.name,
          accountMovePaymentStatus: x.paymentStatus,
          contractId: x.contractId,
          directionSign: x.directionSign,
          status: x.status,
          moveType: x.moveType,
          autoPost: x.autoPost,
          dueDate: x.deliveryDate,
          salesPersonId: x.salesPersonId,
          accountMoveStatus: x.status,
          creationDate: x.creationDate,
          currencyId: x.accountMoveLines[0].currencyId,
          sequence: x.accountMoveLines[0].sequence,
          quantity: x.accountMoveLines[0].quantity,
          unitPrice: x.accountMoveLines[0].unitPrice,
          discount: x.accountMoveLines[0].discount,
          debit: x.accountMoveLines[0].debit,
          credit: x.accountMoveLines[0].credit,
          accountMoveLinePaymentStatus: x.accountMoveLines[0].paymentStatus,
          contactId: x.contactId,
          debitAccountId: x.debitAccountId,
          creditAccountId: x.creditAccountId,
        });
      });

    this.subHeaderService
      .getTriggerAction()
      .pipe(untilDestroyed(this))
      .subscribe((x) => {
        if (x == 'accountMoveCreate' || x === 'accountMoveEdit')
          this.onSubmit();
        this.subHeaderService.setTriggerAction('');
      });

    this.paymentForm.controls['accountMovePeriod']?.valueChanges
      .pipe(debounceTime(150))
      .subscribe((accountMovePeriodName) => {
        this.accountMovePeriodsModels$
          .pipe(filterTrue(), untilDestroyed(this))
          .subscribe((periods) => {
            const period = periods.find((x) => x.name == accountMovePeriodName);
            this.paymentForm.patchValue({
              periodStartDate: formatDate(
                period?.startDate!,
                'ToInverseSqlDate',
              ),
              periodEndDate: formatDate(period?.endDate!, 'ToInverseSqlDate'),
            });
          });
      });

    this.paymentForm.controls['accountMoveName']?.valueChanges
      .pipe(debounceTime(150))
      .subscribe((accountMoveName: string) => {
        this.paymentForm.patchValue({
          accountMoveTypeId: undefined,
          periodStartDate: undefined,
          periodEndDate: undefined,
        });

        //Sur un sinistre le type est forcé et le typeahead est en lecture seule : aucune des branches
        //contrat ne peut s'appliquer. On discrimine sur `insuranceClaimId` et pas sur le libellé, qui
        //est la description localisée de l'enum et change donc avec la culture de la requête.
        if (this.insuranceClaimId) {
          this.paymentForm.patchValue({
            accountMoveTypeId: INSURANCE_CLAIM_FEES_ID,
          });
          this.isRecurringFees = false;
          removeValidators(
            this.paymentForm.get(['periodStartDate'] as const),
            Validators.required,
          );
          removeValidators(
            this.paymentForm.get(['periodEndDate'] as const),
            Validators.required,
          );
          this.accountToDomain = brokerDomain + insuranceCompanyDomain;
          return;
        }

        if (accountMoveName == 'Echéance') {
          this.paymentForm.patchValue({ accountMoveTypeId: RECURRING_FEES_ID });

          this.isRecurringFees = true;
          setValidators(
            this.paymentForm.get(['periodStartDate'] as const),
            Validators.required,
          );
          setValidators(
            this.paymentForm.get(['periodEndDate'] as const),
            Validators.required,
          );
          this.accountToDomain = brokerDomain;
          this.applicationFeeRuleService.getByContractId.reset();
          if (
            this.paymentForm.get(['creditAccountId'])?.value === this.accountId
          ) {
            this.paymentForm.patchValue({
              creditAccountId: undefined,
            });
          }
        } else {
          if (
            accountMoveName == 'Frais de dossier' ||
            accountMoveName == 'Frais de courtage'
          ) {
            this.paymentForm.patchValue({
              accountMoveTypeId: APPLICATION_FEES_ID,
            });
          }
          this.isRecurringFees = false;
          removeValidators(
            this.paymentForm.get(['periodStartDate'] as const),
            Validators.required,
          );
          removeValidators(
            this.paymentForm.get(['periodEndDate'] as const),
            Validators.required,
          );
          if (
            !accountMoveName ||
            accountMoveName == 'Frais de dossier' ||
            accountMoveName == 'Frais de courtage'
          ) {
            this.accountToDomain = brokerDomain + insuranceCompanyDomain;
          } else {
            this.accountToDomain = brokerDomain;
            this.applicationFeeRuleService.getByContractId.reset();
            if (
              this.paymentForm.get(['creditAccountId'])?.value ===
              this.accountId
            ) {
              this.paymentForm.patchValue({
                creditAccountId: undefined,
              });
            }
          }
        }
      });

    this.paymentForm
      .get('creditAccountId')
      ?.valueChanges?.pipe(
        distinctUntilChanged(),
        //la bascule automatique vers les frais de courtage ne concerne que les quittances de contrat.
        filter(() => !this.insuranceClaimId),
        untilDestroyed(this),
      )
      ?.subscribe((value) => {
        if (!!value && !!this.accountId && value === this.accountId) {
          if (
            this.paymentForm.get('accountMoveName')?.value !==
              'Frais de dossier' ||
            this.paymentForm.get('accountMoveName')?.value !==
              'Frais de courtage'
          ) {
            this.paymentForm.patchValue({
              accountMoveName: 'Frais de courtage',
            });
          }
          this.applicationFeeRuleService.getByContractId.call({
            id: this.contractId,
            type: 'Vendor',
          });
        } else {
          if (
            !!value &&
            (this.paymentForm.get('accountMoveName')?.value ===
              'Frais de dossier' ||
              this.paymentForm.get('accountMoveName')?.value ===
                'Frais de courtage')
          ) {
            this.applicationFeeRuleService.getByContractId.call({
              id: this.contractId,
              type: 'Customer',
            });
          }
        }
        if (
          !value ||
          this.paymentForm.get('accountMoveName')?.value !==
            'Frais de dossier' ||
          this.paymentForm.get('accountMoveName')?.value !== 'Frais de courtage'
        ) {
          this.applicationFeeRuleService.getByContractId.reset();
        }
      });

    this.account$
      .pipe(filterTrue(), take(1))
      .subscribe((account: AccountModel) => {
        this.accountId = 0;
        if (account) {
          this.accountId = account.id;
        }
      });

    this.applicationFeeRuleModel$
      .pipe(
        untilDestroyed(this),
        filter((x) => !this.isEdit),
      )
      .subscribe((applicationFeeRule) => {
        if (applicationFeeRule) {
          this.paymentForm.patchValue({
            unitPrice: applicationFeeRule.defaultValue,
          });

          //save min and max
          this.saveValue = {
            minValue: Validators.min(applicationFeeRule.minimumValue),
            maxValue: Validators.max(applicationFeeRule.maximumValue),
          };

          //set min
          setValidators(
            this.paymentForm.get(['unitPrice'] as const),
            this.saveValue.minValue,
          );

          //set max
          setValidators(
            this.paymentForm.get(['unitPrice'] as const),
            this.saveValue.maxValue,
          );

          //update suffix
          this.unitPriceSuffix = getAmountSign(
            this.defaultAmountSign,
            applicationFeeRule.amountType,
          );

          //update placeholder
          this.unitPricePlaceholder =
            this.showSignValueType.transform(
              applicationFeeRule.minimumValue,
              applicationFeeRule.amountType,
            ) +
            ' - ' +
            this.showSignValueType.transform(
              applicationFeeRule.maximumValue,
              applicationFeeRule.amountType,
            );
        } else {
          this.unitPricePlaceholder = this.unitPriceSuffix = getAmountSign(
            this.defaultAmountSign,
          );

          //no rule
          if (this.saveValue) {
            //unset min
            removeValidators(
              this.paymentForm.get(['unitPrice'] as const),
              this.saveValue.minValue,
            );

            //unset max
            removeValidators(
              this.paymentForm.get(['unitPrice'] as const),
              this.saveValue.maxValue,
            );
          }
          //reset errors
          this.paymentForm.get(['unitPrice'] as const)?.setErrors(null);

          if (this.paymentForm.dirty) {
            //remove value
            this.paymentForm.patchValue({
              unitPrice: undefined,
            });

            //reset saveValue
            this.saveValue = undefined;
          }
        }
      });
  }

  onSubmit() {
    if (this.paymentForm.invalid) {
      this.paymentForm.markAllAsTouched();
      this.formService.setEntityErrors('PAYMENT');
      this.formService.countErrors(this.paymentForm, true);
      return;
    }

    let accountMoveLine: AccountMoveLineModel = {
      id: 0,
      accountMoveId: 0,
      currencyId: this.paymentForm.value.currencyId,
      name: this.paymentForm.value.accountMoveLineName,
      unitPrice: this.paymentForm.value.unitPrice,
      paymentModeLoc: '',
      paymentStatusLoc: '',
      sequence: this.paymentForm.value.sequence,
      quantity: this.paymentForm.value.quantity,
      debit: this.paymentForm.value.debit,
      credit: this.paymentForm.value.unitPrice,
      creationDate: new Date(),
      paymentMode: this.paymentForm.value.paymentMode,
      paymentStatus: this.paymentForm.value.accountMoveLinePaymentStatus,
      description: this.paymentForm.value.description,
      discount: this.paymentForm.value.discount,
    };

    let accountMoveLines: AccountMoveLineModel[] = [accountMoveLine];

    let name = this.paymentForm.value.accountMoveName;
    if (this.isRecurringFees) {
      if (this.paymentForm.value.accountMovePeriod != null) {
        name = `${this.paymentForm.value.accountMoveName} ${this.paymentForm.value.accountMovePeriod}`;
      } else {
        name = this.paymentForm.value.accountMoveName;
      }
    }

    let accountMoveModel: AccountMoveModel = {
      id: 0,
      contactId: this.paymentForm.value.contactId,
      currencyId: this.paymentForm.value.currencyId,
      salesPersonId: this.paymentForm.value.salesPersonId,
      creditAccountId: this.paymentForm.value.creditAccountId,
      debitAccountId: this.paymentForm.value.debitAccountId,
      contractId: this.contractId,
      insuranceClaimId: this.insuranceClaimId,
      paymentStatus: this.paymentForm.value.accountMovePaymentStatus,
      paymentStatusLoc: '',
      name: name,
      reference: this.paymentForm.value.accountMoveLineName,
      directionSign: this.paymentForm.value.directionSign,
      status: this.paymentForm.value.accountMoveStatus,
      statusLoc: '',
      moveType: this.paymentForm.value.moveType,
      moveTypeLoc: '',
      paymentReference: this.paymentForm.value.paymentReference,
      autoPost: this.paymentForm.value.autoPost,
      deliveryDate: this.paymentForm.value.dueDate,
      creationDate: new Date(),
      accountMoveLines: accountMoveLines,
      issuingAccountMovesCount: 0,
      payingAccountMovesCount: 0,
      periodEndDate: this.paymentForm.value.periodEndDate,
      periodStartDate: this.paymentForm.value.periodStartDate,
      period: '',
      payingAccountMoves: [],
      issuingAccountMoves: [],
      repaymentAccountMoves: [],
      actions: [],
      journalName: '',
      creditAccountName: '',
      debitAccountName: '',
      accountMoveTypeId: this.paymentForm.value.accountMoveTypeId,
      isReadOnly: false,
      isAdminOnly: false,
    };

    if (this.isClient) {
      accountMoveModel.creditAccountId = this.paymentForm.value.creditAccountId;
    } else {
      accountMoveModel.debitAccountId = this.paymentForm.value.debitAccountId;
    }

    let result$: Observable<AccountMoveModel>;
    if (this.isEdit) {
      let accountMove = deepCopy(this.accountMoveModel);

      accountMove.accountMoveLines[0].unitPrice =
        this.paymentForm.value.unitPrice;
      accountMove.accountMoveLines[0].credit = this.paymentForm.value.unitPrice;
      result$ = this.accountingService.update.call(accountMove);
    } else {
      result$ = this.accountingService.add.call(accountMoveModel);
    }

    result$.pipe(filterTrue(), take(1)).subscribe((result) => {
      this.formService.clear();
      switch (this.source) {
        case SourceEnum.Component:
          // if (result.id == user.contactId) {
          //   this.userService.getCurrentUser.call();
          // }
          // this.router.navigate(['/Contacts/Contacts/Details', result.id]);
          break;
        case SourceEnum.Modal:
          this.dynamicService.outputFromDynamicComponent(
            this.modalRandomIdentifier,
            result.id.toString(),
          );
          break;
        default:
          // this.router.navigate(['/Contacts/Contacts/Details', result.id]);
          break;
      }
    });

    this.formService.clear();
  }

  ngOnDestroy(): void {
    this.accountingService.add.reset();
    this.accountingService.createInstance.reset();
    this.accountingService.getPeriods.reset();
    this.accountingService.getAccountByContactId.reset();
    this.applicationFeeRuleService.getByContractId.reset();
    this.accountingService.getById.reset();
  }
}
